import { Injectable, Inject, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ConversationContext,
  ConversationRequest,
  ConversationResponse,
  CreateConversationRequest,
  SimpleMessageRequest,
  AnalyzeDesignRequest,
  AttachedImage
} from '../../../models/anthropic/anthropic.model';

// Injection token for API base URL
import { API_BASE_URL } from '../../user-favorites.service';

/**
 * Service for interacting with Anthropic Claude AI
 * Provides methods for conversational AI, page generation, and style assistance
 */
@Injectable({
  providedIn: 'root'
})
export class AnthropicService {
  private apiUrl: string;

  constructor(
    private http: HttpClient,
    @Optional() @Inject(API_BASE_URL) private baseUrl?: string
  ) {
    // Use injected base URL or fallback to relative path
    this.apiUrl = this.baseUrl ? `${this.baseUrl}/anthropic` : '/anthropic';
  }

  /**
   * Send a simple message without conversation history
   */
  sendSimpleMessage(message: string, systemPrompt?: string): Observable<{ response: string }> {
    const request: SimpleMessageRequest = {
      message,
      systemPrompt
    };

    return this.http.post<{ response: string }>(
      `${this.apiUrl}/simple-message`,
      request
    );
  }

  /**
   * Create a new conversation
   * Using 'any' to avoid deserialization issues
   */
  createConversation(tenantId: string, purpose: string = 'general', userId?: string, preferencePageId?: string): Observable<any> {
    const request: any = {
      tenantId,
      purpose
    };

    if (userId) {
      request.userId = userId;
    }
    if (preferencePageId) {
      request.preferencePageId = preferencePageId;
    }

    return this.http.post<any>(
      `${this.apiUrl}/conversations`,
      request
    );
  }

  /**
   * Get an existing conversation
   * Using 'any' to avoid deserialization issues
   */
  getConversation(conversationId: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/conversations/${conversationId}`
    );
  }

  /**
   * Get all conversations for a tenant
   * Using 'any' to avoid deserialization issues
   */
  getTenantConversations(tenantId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrl}/conversations/tenant/${tenantId}`
    );
  }

  /**
   * Delete a conversation
   */
  deleteConversation(conversationId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/conversations/${conversationId}`
    );
  }

  /**
   * Get an existing conversation by user, tenant, and preferencePageId.
   * Returns null if no conversation exists (404).
   */
  getConversationByUserAndPage(userId: string, tenantId: string, preferencePageId: string): Observable<any | null> {
    const params = `?userId=${encodeURIComponent(userId)}&tenantId=${encodeURIComponent(tenantId)}&preferencePageId=${encodeURIComponent(preferencePageId)}`;
    return this.http.get<any>(
      `${this.apiUrl}/conversations/user${params}`
    ).pipe(
      catchError((err) => {
        if (err.status === 404) {
          return of(null);
        }
        throw err;
      })
    );
  }

  /**
   * Continue an existing conversation or start a new one
   * Supports text and images
   * Using 'any' to avoid deserialization issues with complex backend responses
   */
  continueConversation(request: ConversationRequest): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/conversations/continue`,
      request
    );
  }

  /**
   * Generate a complete preference page from description
   * Supports text description and reference images
   * Using 'any' to avoid deserialization issues
   */
  generatePreferencePage(request: ConversationRequest): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/generate-page`,
      request
    );
  }

  /**
   * Adjust the CSS styling of a page
   * Supports reference images for desired style
   * Using 'any' to avoid deserialization issues
   */
  adjustPageStyle(request: ConversationRequest): Observable<any> {
    // Ensure context contains html and css
    if (!request.context) {
      throw new Error('Context with html and css is required for style adjustment');
    }

    return this.http.post<any>(
      `${this.apiUrl}/adjust-style`,
      request
    );
  }

  /**
   * Adjust the JavaScript of a page
   * Using 'any' to avoid deserialization issues
   */
  adjustPageScript(request: ConversationRequest): Observable<any> {
    // Ensure context contains html
    if (!request.context) {
      throw new Error('Context with html is required for script adjustment');
    }

    return this.http.post<any>(
      `${this.apiUrl}/adjust-script`,
      request
    );
  }

  /**
   * Analyze a design image and extract style information
   */
  analyzeDesign(image: AttachedImage, prompt?: string): Observable<{ analysis: string }> {
    const request: AnalyzeDesignRequest = {
      image,
      prompt: prompt || 'Analyze this design and provide CSS recommendations to recreate it'
    };

    return this.http.post<{ analysis: string }>(
      `${this.apiUrl}/analyze-design`,
      request
    );
  }

  /**
   * Helper: Convert File to AttachedImage
   */
  async fileToAttachedImage(file: File): Promise<AttachedImage> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        const base64Data = base64.split(',')[1]; // Remove data:image/xxx;base64, prefix
        
        resolve({
          base64Data,
          mediaType: file.type,
          fileName: file.name
        });
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Helper: Convert multiple files to AttachedImages
   */
  async filesToAttachedImages(files: File[]): Promise<AttachedImage[]> {
    return Promise.all(files.map(file => this.fileToAttachedImage(file)));
  }
}
